import { BookingAnalyticsApi } from './bookingAnalyticsApi';

/**
 * Fetch paginated trace logs.
 * Endpoint (prod pattern): GET /api/admin/v1/trace-logs/page/{page}
 *
 * Response shape (current API):
 *   { success: true, data: { data: [...], meta: { total, per_page, current_page, last_page, last_updated }, summary: { total, confirmed, expired, abandoned, failed, pending } } }
 *
 * @param {number} page  Page number (1-indexed)
 * @param {{ startDate?: string, endDate?: string, search?: string, status?: string, module?: string, per_page?: number }} filters
 */
export async function fetchTraceLogs(page = 1, filters = {}) {
  const params = { page, per_page: filters.per_page || 20 };

  // Map legacy filter key names → API param names (prod uses start_date / end_date)
  if (filters.startDate) params.start_date = filters.startDate;
  if (filters.endDate)   params.end_date   = filters.endDate;
  if (filters.search)    params.search     = filters.search;
  if (filters.status  && filters.status  !== 'all') params.status = filters.status;
  if (filters.module  && filters.module  !== 'all') params.module = filters.module;

  const response = await BookingAnalyticsApi.getLogs(params);

  if (!response?.success) {
    throw new Error(response?.message || 'Failed to fetch trace logs');
  }

  // response.data is the paginated envelope:
  //   Shape A (current API): { data: [...], meta: { total, per_page, current_page, last_page }, summary: {...} }
  //   Shape B (old API):      { data: [...], current_page, last_page, total, per_page, from, to }
  const envelope = response.data;
  return {
    resultData: envelope,
    statsData:  response.status_summary || envelope?.summary || null,
  };
}

// ── Transform helpers ────────────────────────────────────────────────────────

/** Transform API response to match the format expected by components */
export function transformTraceLog(apiLog) {
  const lastStep   = apiLog.steps?.[apiLog.steps.length - 1];
  const failedStep = apiLog.steps?.find((s) => s.step_status === 'failed');

  return {
    id:            apiLog.id?.toString(),
    correlationId: apiLog.session_id,
    bookingRef:    apiLog.record_id || null,
    module:        apiLog.module,
    logName:       apiLog.log_name,
    status:        mapStatus(apiLog.status),
    severity:      apiLog.severity,
    lastStep:      lastStep?.step || 'Unknown',
    failedStep:    failedStep?.step || null,
    startedAt:     apiLog.created_at,
    completedAt:   apiLog.updated_at,
    ipAddress:     apiLog.ip_address,
    userAgent:     apiLog.user_agent,
    meta:          apiLog.meta,
    steps:         transformSteps(apiLog.steps),
  };
}

function mapStatus(apiStatus) {
  const statusMap = {
    pending:   'IN_PROGRESS',
    expired:   'EXPIRED',
    abandoned: 'ABANDONED_CHECKOUT',
    failed:    'FAILED',
    confirmed: 'CONFIRMED',
    success:   'CONFIRMED',
  };
  return statusMap[apiStatus?.toLowerCase()] || apiStatus?.toUpperCase() || 'UNKNOWN';
}

function transformSteps(apiSteps) {
  if (!apiSteps || !Array.isArray(apiSteps)) return [];
  return apiSteps.map((step, index) => ({
    id:           (index + 1).toString(),
    stepName:     step.step,
    actor:        'System',
    description:  step.response?.message || '',
    status:
      step.step_status === 'success' ? 'completed' :
      step.step_status === 'failed'  ? 'failed'    : 'pending',
    timestamp:    step.timestamp,
    duration:     step.time_ms || null,
    request:      step.request  || null,
    response:     step.response || null,
    metadata:     step.meta     || null,
    errorCode:    step.step_status === 'failed' ? 'STEP_FAILED'                       : null,
    errorMessage: step.step_status === 'failed' ? (step.response?.message || 'Step failed') : null,
  }));
}

/**
 * Normalise the paginated envelope regardless of response shape:
 *   Shape A (current API): { data: [...], meta: { total, per_page, current_page, last_page } }
 *   Shape B (old API):      { data: [...], current_page, last_page, total, per_page, from, to }
 */
export function transformTraceLogsResponse(apiResponse) {
  // Detect shape A vs shape B
  const hasMeta = apiResponse?.meta && typeof apiResponse.meta === 'object';
  const paginationSrc = hasMeta ? apiResponse.meta : apiResponse;
  // Shape A nests sessions under data.data; Shape B has them at data.data (same)
  const rows = apiResponse?.data ?? [];

  return {
    logs: rows.map(transformTraceLog),
    pagination: {
      currentPage: paginationSrc?.current_page  ?? 1,
      lastPage:    paginationSrc?.last_page      ?? 1,
      perPage:     paginationSrc?.per_page       ?? 20,
      total:       paginationSrc?.total          ?? 0,
      from:        paginationSrc?.from           ?? null,
      to:          paginationSrc?.to             ?? null,
      lastUpdated: hasMeta ? apiResponse.meta?.last_updated : null,
    },
  };
}
